import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { debounce } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers";
import { fr, enUS, es } from "date-fns/locale";
import styles from "./CustomFormDate.module.css";
import { CustomDatePicker, CustomTextField } from "../../../..";
import { isValidDate } from "../../../../../utils";
import { useApi } from "../../../../../hooks";
import { CustomizableProjectPagesService } from "../../../../../../api";
import { FORM_TYPES } from "../../../form-enum";
import { translateDate } from "../../../../../providers";
import { SectionsContext } from "../../../CustomizableForm";

function getValidDate(input) {
	const date = new Date(input);
	return Number.isNaN(date.getTime()) ? null : date;
}

const debouncedFunction = debounce((func) => func(), 500);

export default function CustomFormDate({
	block,
	customPageId,
	hasEditorRights = false,
	isFilter = false,
	projectId,
	sectionId,
	onGlobalUpdate,
}) {
	const [localValue, setLocalValue] = useState("");
	const language = useSelector(({ context }) => context.language.value);
	const customDateRef = useRef(null);
	const { call: updateCustomPage } = useApi(CustomizableProjectPagesService.updateCustomPage);
	const { updateLocalSection } = useContext(SectionsContext);
	const { currentValue } = block.options;
	const { id } = block;
	const localeLanguage = useMemo(
		() => (language === "en" && enUS) || (language === "fr" && fr) || (language === "esp" && es) || enUS,
		[language]
	);
	useEffect(() => {
		if (currentValue) {
			setLocalValue(currentValue);
		}
	}, [currentValue]);

	const displayDateTime = (savedTime) => {
		if (isValidDate(getValidDate(savedTime))) {
			return format(savedTime, "yyyy-MM-dd HH:mm:ss");
		}
		return "";
	};
	const handleSubmit = (newValue) => {
		const newDate = displayDateTime(newValue);
		if (currentValue !== newValue) {
			if (onGlobalUpdate && typeof onGlobalUpdate === "function") {
				onGlobalUpdate({
					uuid: id,
					value: newDate,
				});
				updateLocalSection(sectionId, block.id, { currentValue: newDate });
			} else {
				updateCustomPage({ customPageId, projectId }, { uuid: id, value: newDate })
					.then(() => {
						updateLocalSection(sectionId, block.id, { currentValue: newDate });
					})
					.catch(console.error);
			}
		}
	};
	const handleChangeDate = (dateObject) => {
		const newValue = dateObject.toISOString();
		setLocalValue(newValue);
		debouncedFunction(() => handleSubmit(newValue));
	};
	const handleDateChange = (newValue) => {
		setLocalValue(newValue);
		debouncedFunction(() => handleSubmit(newValue));
	};
	return (
		<div
			ref={customDateRef}
			className={styles.container}
			data-has-editor-right={hasEditorRights}
			id={id}
			role="presentation"
		>
			{(hasEditorRights && (
				<LocalizationProvider adapterLocale={localeLanguage} dateAdapter={AdapterDateFns}>
					{(block.type === FORM_TYPES.DATE && (
						<CustomDatePicker
							fullWidth
							className={styles.datePicker}
							disabled={(!hasEditorRights && !isFilter) || block?.readOnly}
							inputFormat="dd/MM/yyyy"
							value={getValidDate(localValue)}
							onChange={handleChangeDate}
						/>
					)) || (
						<DatePicker
							disabled={(!hasEditorRights && !isFilter) || block?.readOnly}
							format="dd/MM/yyyy"
							slotProps={{ textField: { fullWidth: true } }}
							textField={(params) => <CustomTextField {...params} />}
							value={getValidDate(localValue)}
							onChange={handleDateChange}
						/>
					)}
				</LocalizationProvider>
			)) || <span>{translateDate(localValue)}</span>}
		</div>
	);
}
